import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, Search, Link, Instagram, Youtube, Facebook, Globe, Mail, Share2, MoreVertical, Pencil, Trash2, Camera } from "lucide-react";
import { TikTokIcon, XIcon, SubstackIcon, ThreadsIcon } from '../components/SocialIcons';
import RecipeCard from '../components/RecipeCard';
import RecipeForm from '../components/RecipeForm';
import OwnerDashboard from '../components/OwnerDashboard';
import UpgradeModal from '../components/UpgradeModal';
import PaymentConfirmationDialog from '../components/PaymentConfirmationDialog';
import RecipeCardSkeleton from '../components/RecipeCardSkeleton';
import RecipeCollectionGrid from '../components/RecipeCollectionGrid';
import EmptyCollectionState from '../components/EmptyCollectionState';
import { getCollectionDisplayTitle } from '../utils/collectionTitle';
import { getAuthConfig } from '../utils/authSession';
import { API } from '../utils/constants';
import { trackShareClick } from '../utils/track';
import recipeCache from '../utils/recipeCache';
import { saveListReturnState, readListReturnState, clearListReturnState } from '../utils/listReturnState';
import { filterRecipesBySearch } from '../utils/recipeSearch';
import {
  confirmSubscriptionAfterPayment,
  removePaymentResultFromUrl,
} from '../utils/subscriptionConfirmation';
import { isValidSocialUrl, normalizeSocialUrl, PLATFORM_PLACEHOLDERS } from '../lib/socialLinks';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Category Item Component
const SortableCategoryItem = ({
  category,
  editingCategoryId,
  editingCategoryName,
  setEditingCategoryId,
  setEditingCategoryName,
  handleUpdateCategory,
  handleDeleteCategory
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: '#F3EEE4',
    border: '1px solid #D6D0C2'
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between p-3 rounded hover:bg-[#EEE7D8] transition-colors"
    >
      <div className="flex items-center gap-3 flex-1">
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className="cursor-move"
          style={{ color: '#51120A', opacity: 0.5 }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <circle cx="4" cy="4" r="1.5" />
            <circle cx="4" cy="8" r="1.5" />
            <circle cx="4" cy="12" r="1.5" />
            <circle cx="12" cy="4" r="1.5" />
            <circle cx="12" cy="8" r="1.5" />
            <circle cx="12" cy="12" r="1.5" />
          </svg>
        </div>

        {/* Category Name */}
        {editingCategoryId === category.id ? (
          <Input
            value={editingCategoryName}
            onChange={(e) => setEditingCategoryName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleUpdateCategory(category.id, editingCategoryName);
              }
            }}
            onBlur={() => handleUpdateCategory(category.id, editingCategoryName)}
            autoFocus
            className="flex-1"
            style={{
              backgroundColor: '#EEE7D8',
              borderColor: '#D6D0C2',
              color: '#51120A',
              fontFamily: "'Bricolage Grotesque', sans-serif"
            }}
          />
        ) : (
          <span style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
            {category.name}
          </span>
        )}
      </div>

      {/* Actions Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="p-1 hover:bg-[#D6D0C2] rounded">
            <MoreVertical size={16} style={{ color: '#51120A' }} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" style={{ backgroundColor: '#F3EEE4', border: '1px solid #D6D0C2', boxShadow: 'none' }}>
          <DropdownMenuItem
            onClick={() => {
              console.log('EDIT CLICKED - Category ID:', category.id, 'Category:', category);
              setEditingCategoryId(category.id);
              setEditingCategoryName(category.name);
            }}
            className="hover:bg-[#EEE7D8] cursor-pointer"
            style={{ color: '#875C66', fontFamily: "'Bricolage Grotesque', sans-serif" }}
          >
            <Pencil size={14} className="mr-2" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              console.log('DELETE CLICKED - Category ID:', category.id);
              handleDeleteCategory(category.id);
            }}
            className="hover:bg-[#EEE7D8] cursor-pointer"
            style={{ color: '#875C66', fontFamily: "'Bricolage Grotesque', sans-serif" }}
          >
            <Trash2 size={14} className="mr-2" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

const RecipeCollection = () => {
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [filteredRecipes, setFilteredRecipes] = useState([]);
  // If the user is returning from a recipe they opened, restore the category
  // they had selected (and, below, their scroll position) instead of
  // resetting to the top of All Recipes.
  const [returnState] = useState(() => readListReturnState('own'));
  const [selectedCategory, setSelectedCategory] = useState(returnState?.selectedCategory || "all");
  const [searchQuery, setSearchQuery] = useState("");
  const [isAISearching, setIsAISearching] = useState(false);
  const [adminToken, setAdminToken] = useState(null);
  const [authResolved, setAuthResolved] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [showRecipeForm, setShowRecipeForm] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [recipeToDelete, setRecipeToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [profile, setProfile] = useState({
    collection_title: "Recipe Collection",
    bio: "",
    creator_name: "",
    creator_photo_url: "",
    social_links: {},
    social_display_names: {}
  });
  const [profileForm, setProfileForm] = useState({ bio: "", social_links: {}, social_display_names: {} });
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [linkErrors, setLinkErrors] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  const [showOwnerDashboard, setShowOwnerDashboard] = useState(false);
  // Seed categories from the last successful fetch so the filter row shows
  // instantly on page load instead of popping in after the network call.
  const [categories, setCategories] = useState(() => {
    try {
      const cached = localStorage.getItem('cachedCategories');
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });
  const [showManageCategories, setShowManageCategories] = useState(false);
  const [newCategoryInput, setNewCategoryInput] = useState('');
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');
  const [deleteCategoryDialog, setDeleteCategoryDialog] = useState({ open: false, category: null, recipeCount: 0 });
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState('none');
  const [trialEnd, setTrialEnd] = useState(null);
  const [paymentConfirmationState, setPaymentConfirmationState] = useState(() => (
    new URLSearchParams(window.location.search).get('payment') === 'success'
      ? 'checking'
      : null
  ));
  const [paymentConfirmationAttempt, setPaymentConfirmationAttempt] = useState(0);
  const fileInputRef = React.useRef(null);
  const mountedRef = useRef(true);
  const recipeMutationRevisionRef = useRef(0);
  const recipeRefreshRef = useRef({ inFlight: null, accountKey: null, lastStartedAt: 0 });
  const pinRequestsRef = useRef(new Set());

  const getRecipeAccount = () => {
    try {
      const token = localStorage.getItem('adminToken');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!token || !user?.id) return null;
      // Request guards follow the account, not the short-lived access token.
      // Axios may transparently refresh that token before resolving a request;
      // the successful retry still belongs to this same user.
      return { token, user, key: String(user.id) };
    } catch {
      return null;
    }
  };

  const cacheOwnerRecipes = (next, expectedAccountKey = null) => {
    const account = getRecipeAccount();
    if (!account || (expectedAccountKey && account.key !== expectedAccountKey)) return;
    recipeCache.setList(`user:${account.user.id}`, next, account.user.username);
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    let cancelled = false;
    getAuthConfig().then((config) => {
      if (cancelled) return;
      const token = config?.headers?.Authorization?.replace(/^Bearer\s+/i, '');
      const account = getRecipeAccount();
      if (token && account) {
        setAdminToken(token);
        setAuthResolved(true);
      } else {
        navigate("/");
      }
    });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) return;

      const response = await axios.get(`${API}/my-profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
      setProfileForm(response.data);
      // Keep the stored user in sync with the server (handle may have
      // changed on another device) so /username owner-detection stays fresh.
      try {
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (response.data.username && storedUser && storedUser.username !== response.data.username) {
          localStorage.setItem('user', JSON.stringify({ ...storedUser, username: response.data.username }));
        }
      } catch { /* ignore corrupted localStorage */ }
      // Canonicalize the URL: the owner's page lives at /username so the
      // address bar always shows the shareable link. Keep /recipes (old
      // bookmarks, payment return URLs) and stale handles working, but
      // forward them to the current handle.
      if (response.data.username) {
        const canonical = `/${encodeURIComponent(response.data.username)}`;
        const path = window.location.pathname;
        const isSingleSegment = /^\/[^/]+$/.test(path);
        if (path === '/recipes' || (isSingleSegment && path !== canonical)) {
          navigate(`${canonical}${window.location.search}`, { replace: true });
        }
      }
    } catch (error) {
      console.error("Failed to fetch profile", error);
    } finally {
      // Keep the page skeleton up until the bio/profile has arrived so the
      // header doesn't flash empty ("jump scare") before the data loads.
      setProfileLoaded(true);
    }
  };

  const fetchRecipes = ({ showCached = false, eventRefresh = false } = {}) => {
    const account = getRecipeAccount();
    if (!account) {
      if (mountedRef.current) setLoading(false);
      return Promise.resolve();
    }

    const cacheKey = `user:${account.user.id}`;
    if (showCached) {
      const cached = recipeCache.getList(cacheKey);
      if (cached) {
        setRecipes(cached);
        setFilteredRecipes(cached);
        setLoading(false);
      }
    }

    const refresh = recipeRefreshRef.current;
    if (refresh.inFlight && refresh.accountKey === account.key) {
      return refresh.inFlight;
    }
    // Browsers commonly emit visibilitychange and focus together. One
    // authoritative request is enough for that pair.
    if (eventRefresh && refresh.accountKey === account.key && Date.now() - refresh.lastStartedAt < 1000) {
      return Promise.resolve();
    }

    const requestRevision = recipeMutationRevisionRef.current;
    const request = axios.get(`${API}/recipes?user_id=${account.user.id}`, {
      headers: { Authorization: `Bearer ${account.token}` }
    }).then((response) => {
      const currentAccount = getRecipeAccount();
      if (
        !mountedRef.current
        || !currentAccount
        || currentAccount.key !== account.key
        || recipeMutationRevisionRef.current !== requestRevision
      ) {
        return;
      }

      setRecipes(response.data);
      setFilteredRecipes(response.data);
      recipeCache.setList(cacheKey, response.data, account.user.username);
    }).catch((error) => {
      console.error("Failed to fetch recipes", error);
    }).finally(() => {
      if (recipeRefreshRef.current.inFlight === request) {
        recipeRefreshRef.current.inFlight = null;
      }
      const currentAccount = getRecipeAccount();
      if (mountedRef.current && currentAccount?.key === account.key) {
        setLoading(false);
      }
    });

    recipeRefreshRef.current = {
      inFlight: request,
      accountKey: account.key,
      lastStartedAt: Date.now()
    };
    return request;
  };

  // Update categories in state AND the localStorage seed cache together so a
  // later page load never shows stale categories from before an edit.
  const persistCategories = (next) => {
    setCategories(next);
    try {
      localStorage.setItem('cachedCategories', JSON.stringify(next));
    } catch { /* storage full/unavailable — cache is best-effort */ }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) return;

      const response = await axios.get(`${API}/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Sort categories by order field
      const sortedCategories = response.data.sort((a, b) => (a.order || 0) - (b.order || 0));
      setCategories(sortedCategories);
      try {
        localStorage.setItem('cachedCategories', JSON.stringify(sortedCategories));
      } catch { /* storage full/unavailable — cache is best-effort */ }
    } catch (error) {
      console.error("Failed to fetch categories", error);
      // Only fall back to defaults if we have nothing to show — don't wipe
      // out categories we already have (from cache or a previous fetch).
      setCategories(prev => (prev && prev.length > 0) ? prev : [
        { id: 'breakfast', name: 'Breakfast', order: 0 },
        { id: 'lunch', name: 'Lunch', order: 1 },
        { id: 'dinner', name: 'Dinner', order: 2 }
      ]);
    }
  };

  const fetchSubscription = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) return;
      const response = await axios.get(`${API}/subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionStatus(response.data.status || 'none');
      setTrialEnd(response.data.trial_end || null);
    } catch (error) {
      console.error("Failed to fetch subscription", error);
    }
  };

  const requireActiveSubscription = () => {
    if (subscriptionStatus === 'expired') {
      setShowUpgradeModal(true);
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (!authResolved) return;
    // Cache is only the instant first paint. Always revalidate it against the
    // owner's authoritative list so a briefly stale read cannot hide a pin.
    fetchRecipes({ showCached: true });
    fetchProfile();
    fetchCategories();
    if (new URLSearchParams(window.location.search).get('payment') !== 'success') {
      fetchSubscription();
    }
  }, [authResolved]);

  useEffect(() => {
    if (!authResolved) return undefined;
    const refreshVisibleRecipes = () => {
      if (document.visibilityState === 'visible') {
        fetchRecipes({ eventRefresh: true });
      }
    };
    window.addEventListener('focus', refreshVisibleRecipes);
    document.addEventListener('visibilitychange', refreshVisibleRecipes);
    return () => {
      window.removeEventListener('focus', refreshVisibleRecipes);
      document.removeEventListener('visibilitychange', refreshVisibleRecipes);
    };
  }, [authResolved]);

  // Called just before navigating into a recipe so the return trip can
  // land back on the same category and scroll position.
  const saveReturnState = () => saveListReturnState('own', { selectedCategory });

  // Save-flow v2 confirmation: landing here right after saving someone
  // else's recipe scrolls to the new card and briefly highlights it —
  // seeing it in your own Wellby IS the confirmation.
  const location = useLocation();
  const highlightedRef = React.useRef(false);

  useEffect(() => {
    const paymentResult = new URLSearchParams(location.search).get('payment');
    if (paymentResult !== 'success') return undefined;

    let cancelled = false;
    const token = localStorage.getItem('adminToken');
    if (!token) return undefined;

    setPaymentConfirmationState('checking');

    confirmSubscriptionAfterPayment({
      getSubscription: async () => {
        const response = await axios.get(
          `${API}/subscription`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        return response.data;
      },
      confirmSubscription: async () => {
        const response = await axios.post(
          `${API}/subscription/confirm`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
        return response.data;
      },
    }).then((result) => {
      if (cancelled) return;

      if (result.state === 'success') {
        setSubscriptionStatus('active');
        setTrialEnd(result.subscription?.trial_end || null);
        setPaymentConfirmationState('success');

        window.history.replaceState(
          {},
          '',
          removePaymentResultFromUrl(window.location.href),
        );
        return;
      }

      setPaymentConfirmationState('pending');
    });

    return () => {
      cancelled = true;
    };
  }, [location.search, paymentConfirmationAttempt]);

  const closePaymentConfirmation = () => {
    setPaymentConfirmationState(null);
    window.history.replaceState(
      {},
      '',
      removePaymentResultFromUrl(window.location.href),
    );
  };

  useEffect(() => {
    const highlightId = location.state?.highlightRecipeId;
    if (!highlightId || loading || highlightedRef.current) return;
    if (!recipes.some(r => r.id === highlightId)) return;
    // A remembered category filter could hide the new card — show all so
    // the confirmation (scroll + highlight) is always visible.
    if (selectedCategory !== 'all') {
      setSelectedCategory('all');
      return; // effect re-runs after the filter change renders the card
    }
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-testid="recipe-card-${highlightId}"]`);
      if (!el) return; // not rendered yet — try again on the next pass
      // Only consume the highlight once the card is actually on screen
      highlightedRef.current = true;
      // Clear the state so refresh/back doesn't re-trigger the highlight
      window.history.replaceState({}, '');
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('save-highlight');
      setTimeout(() => el.classList.remove('save-highlight'), 2600);
    });
  }, [location.state, loading, recipes, selectedCategory]);

  // Restore scroll position once the page content has actually rendered
  const scrollRestoredRef = React.useRef(false);
  useEffect(() => {
    if (loading || !profileLoaded || !returnState || scrollRestoredRef.current) return;
    scrollRestoredRef.current = true;
    clearListReturnState('own');
    requestAnimationFrame(() => window.scrollTo(0, returnState.scrollY || 0));
  }, [loading, profileLoaded, returnState]);

  useEffect(() => {
    let filtered = recipes;

    const hasSearch = !isAISearching && searchQuery.trim();

    if (selectedCategory !== "all" && !hasSearch) {
      filtered = filtered.filter(r =>
        r.categories && r.categories.some(cat => cat.toLowerCase() === selectedCategory.toLowerCase())
      );
    }

    if (hasSearch) {
      filtered = filterRecipesBySearch(filtered, searchQuery);
    }

    setFilteredRecipes(filtered);
  }, [selectedCategory, searchQuery, recipes, isAISearching]);

  const handleAISearch = async () => {
    if (!searchInput.trim()) {
      setIsAISearching(false);
      return;
    }

    setIsAISearching(true);
    try {
      // /api/ai-search now requires auth (the endpoint scans the full
      // recipe table and calls gpt-4o per request, so it had to move
      // behind the per-user AI quota to stop a financial-DDoS via IP
      // rotation). Send the bearer token alongside the request.
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${API}/ai-search`,
        { query: searchInput },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      setFilteredRecipes(response.data.results);
      toast.success("AI search complete!");
    } catch (error) {
      console.error("AI search failed", error);
      toast.error("AI search failed, using basic search");
      setIsAISearching(false);
    }
  };

  const searchTimeoutRef = React.useRef(null);
  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    setIsAISearching(false);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(value);
    }, 300);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload-photo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${adminToken}`
        }
      });

      setProfileForm({ ...profileForm, creator_photo_url: response.data.photo_url });
      toast.success("Photo uploaded! Click Save to apply changes.");
    } catch (error) {
      console.error("Failed to upload photo", error);
      toast.error("Failed to upload photo");
    }
  };

  const handleSaveProfile = async () => {
    // Validate social links: flag invalid ones and block save.
    const links = profileForm.social_links || {};
    const errors = {};
    const normalizedLinks = {};
    Object.entries(links).forEach(([key, value]) => {
      const trimmed = (value || '').trim();
      if (!trimmed) {
        normalizedLinks[key] = '';
        return;
      }
      if (isValidSocialUrl(trimmed, key)) {
        normalizedLinks[key] = normalizeSocialUrl(trimmed);
      } else {
        errors[key] = true;
      }
    });
    if (Object.keys(errors).length > 0) {
      setLinkErrors(errors);
      toast.error("Invalid link");
      return;
    }
    setLinkErrors({});
    const payload = { ...profileForm, social_links: normalizedLinks };
    try {
      const response = await axios.put(`${API}/profile`, payload, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      // Merge: the PUT response omits some fields (is_owner, subscription info),
      // so keep existing state and overlay the updated values.
      const updatedProfile = { ...profile, ...response.data };
      setProfile(updatedProfile);
      setProfileForm(prev => ({ ...prev, ...response.data }));
      recipeCache.setProfile(updatedProfile.username || updatedProfile.id, updatedProfile);
      setIsEditingProfile(false);
      toast.success("Profile saved!");
    } catch (error) {
      console.error("Failed to update profile", error);
      toast.error("Failed to update profile");
    }
  };

  const handleCancelEdit = () => {
    setProfileForm(profile);
    setIsEditingProfile(false);
  };

  const handleShareCollection = () => {
    if (!requireActiveSubscription()) return;
    // Build the public collection URL using username
    if (profile?.username) {
      const publicUrl = `${window.location.origin}/${profile.username}`;
      navigator.clipboard.writeText(publicUrl);
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
    trackShareClick();
    toast.success("Link copied to clipboard!");
  };

  const handleEditRecipe = (recipe) => {
    if (!requireActiveSubscription()) return;
    setEditingRecipe(recipe);
    setShowRecipeForm(true);
  };

  const handleDeleteRecipe = (recipeId) => {
    if (!requireActiveSubscription()) return;
    setRecipeToDelete(recipeId);
    setShowDeleteDialog(true);
  };

  const handleTogglePin = async (recipeId) => {
    if (pinRequestsRef.current.has(recipeId)) return;
    const account = getRecipeAccount();
    if (!account) return;
    pinRequestsRef.current.add(recipeId);
    try {
      const response = await axios.patch(
        `${API}/recipes/${recipeId}/pin`,
        {},
        { headers: { Authorization: `Bearer ${account.token}` } }
      );

      if (!mountedRef.current || getRecipeAccount()?.key !== account.key) return;
      recipeMutationRevisionRef.current += 1;
      setRecipes(prev => {
        const next = prev.map(r =>
          r.id === recipeId ? { ...r, pinned: response.data.pinned } : r
        );
        // Keep the session cache in sync so navigating away and back
        // doesn't resurrect the old pin state (previously required a
        // hard refresh to clear).
        cacheOwnerRecipes(next, account.key);
        return next;
      });

      toast.success(response.data.pinned ? "Recipe pinned!" : "Recipe unpinned!");
    } catch (error) {
      console.error("Failed to toggle pin", error);
      toast.error("Failed to update pin status");
    } finally {
      pinRequestsRef.current.delete(recipeId);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryInput.trim()) return;

    try {
      const response = await axios.post(
        `${API}/categories`,
        { name: newCategoryInput.trim() },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );

      persistCategories([...categories, response.data]);
      setNewCategoryInput('');
      toast.success('Category created!');
    } catch (error) {
      console.error("Failed to create category", error);
      toast.error("Failed to create category");
    }
  };

  const handleUpdateCategory = async (categoryId, newName) => {
    try {
      await axios.put(
        `${API}/categories/${categoryId}`,
        { name: newName },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );

      persistCategories(categories.map(cat =>
        cat.id === categoryId ? { ...cat, name: newName } : cat
      ));
      setEditingCategoryId(null);
      toast.success('Category updated!');
    } catch (error) {
      console.error("Failed to update category", error);
      toast.error("Failed to update category");
    }
  };

  const handleDeleteCategory = (categoryId) => {
    // Find the category to get its name
    const category = categories.find(cat => cat.id === categoryId);
    if (!category) return;

    // Count how many recipes use this category
    const recipeCount = recipes.filter(r => r.category === category.name.toLowerCase()).length;

    // Open custom delete dialog
    setDeleteCategoryDialog({
      open: true,
      category: category,
      recipeCount: recipeCount
    });
  };

  const confirmDeleteCategory = async () => {
    const { category } = deleteCategoryDialog;
    if (!category) return;

    try {
      await axios.delete(
        `${API}/categories/${category.id}`,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );

      persistCategories(categories.filter(cat => cat.id !== category.id));
      setDeleteCategoryDialog({ open: false, category: null, recipeCount: 0 });
      toast.success('Category deleted!');
      fetchRecipes(); // Refresh recipes
    } catch (error) {
      console.error("Failed to delete category", error);
      toast.error("Failed to delete category");
      setDeleteCategoryDialog({ open: false, category: null, recipeCount: 0 });
    }
  };

  const handleDragEnd = async (event) => {
    console.log('DRAG END EVENT:', event);
    const { active, over } = event;

    if (!over || active.id === over.id) {
      console.log('DRAG END - no movement or no target');
      return;
    }

    const oldIndex = categories.findIndex((cat) => cat.id === active.id);
    const newIndex = categories.findIndex((cat) => cat.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newCategories = arrayMove(categories, oldIndex, newIndex);
    persistCategories(newCategories);

    // Update order in backend
    try {
      await axios.put(
        `${API}/categories/reorder`,
        {
          category_ids: newCategories.map(cat => cat.id)
        },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success('Category order updated!');
    } catch (error) {
      console.error("Failed to update category order", error);
      toast.error("Failed to update category order");
      // Revert on error
      fetchCategories();
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const confirmDelete = async () => {
    if (!recipeToDelete) return;
    const account = getRecipeAccount();
    if (!account) return;

    try {
      await axios.delete(`${API}/recipes/${recipeToDelete}`, {
        headers: { Authorization: `Bearer ${account.token}` }
      });
      if (!mountedRef.current || getRecipeAccount()?.key !== account.key) return;
      recipeMutationRevisionRef.current += 1;
      setRecipes(prev => {
        const next = prev.filter(recipe => recipe.id !== recipeToDelete);
        recipeCache.invalidate(recipeToDelete);
        cacheOwnerRecipes(next, account.key);
        return next;
      });
      toast.success("Recipe deleted successfully!");
    } catch (error) {
      toast.error("Failed to delete recipe");
    } finally {
      setShowDeleteDialog(false);
      setRecipeToDelete(null);
    }
  };

  const handleSaveRecipe = (savedRecipe) => {
    setShowRecipeForm(false);
    setEditingRecipe(null);
    if (savedRecipe && savedRecipe.id) {
      const account = getRecipeAccount();
      recipeMutationRevisionRef.current += 1;
      // Merge the saved recipe into the list immediately — a refetch right
      // after a write can return stale data (DB reads lag writes briefly),
      // which is why new recipes previously needed a hard refresh to appear.
      setRecipes(prev => {
        const exists = prev.some(r => r.id === savedRecipe.id);
        const next = exists
          ? prev.map(r => (r.id === savedRecipe.id ? savedRecipe : r))
          : [savedRecipe, ...prev];
        cacheOwnerRecipes(next, account?.key);
        return next;
      });
    } else {
      recipeCache.invalidateLists();
      fetchRecipes();
    }
    fetchCategories();
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('cachedCategories');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setAdminToken(null);
    toast.success("Logged out");
    navigate("/");
  };

  // Skeleton loader while loading (wait for both recipes and profile so the
  // bio/header never flashes empty before its data arrives)
  if (loading || !profileLoaded) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#EEE7D8' }}>
        <div className="max-w-7xl mx-auto px-4 py-16">
          {/* Header Skeleton */}
          <div className="mb-8">
            <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-4 gap-4">
              {/* Title skeleton */}
              <div className="h-12 w-56 rounded" style={{ backgroundColor: '#D6D0C2' }} />
              <div className="flex gap-3">
                {/* Button skeletons */}
                <div className="h-10 w-28 rounded" style={{ backgroundColor: '#F3EEE4' }} />
                <div className="h-10 w-10 rounded" style={{ backgroundColor: '#F3EEE4' }} />
                <div className="h-10 w-10 rounded" style={{ backgroundColor: '#F3EEE4' }} />
              </div>
            </div>
          </div>

          {/* Category Filters Skeleton */}
          <div className="flex gap-3 mb-8 overflow-x-auto pb-2">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-8 w-24 rounded-full flex-shrink-0" style={{ backgroundColor: '#F3EEE4' }} />
            ))}
          </div>

          {/* Recipe Cards Skeleton - Desktop (mixed heights, like real cards) */}
          <RecipeCollectionGrid>
            {[true, false, true, false, false, true].map((withImage, i) => (
              <RecipeCardSkeleton
                key={i}
                withImage={withImage}
              />
            ))}
          </RecipeCollectionGrid>

          {/* Recipe Cards Skeleton - Mobile */}
          <div className="md:hidden space-y-6">
            {[true, false, false, true].map((withImage, i) => (
              <RecipeCardSkeleton key={i} withImage={withImage} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const getTrialDaysRemaining = () => {
    if (!trialEnd) return null;
    try {
      const end = new Date(trialEnd);
      const now = new Date();
      const days = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
      return Math.max(0, days);
    } catch { return null; }
  };

  const trialDaysRemaining = getTrialDaysRemaining();

  // Blank-state helpers: before the profile is filled in, show
  // "Name's recipes" as the title and a prompt to edit the profile.
  // Add ?previewBlank=1 to the URL to preview the new-user blank state.
  const previewBlank = new URLSearchParams(window.location.search).get('previewBlank') === '1';
  // "Empty" means no bio, photo, or social links yet (the name alone is
  // preloaded from signup, so it doesn't count).
  // Show the "personalize your page" prompt whenever there's no bio and no
  // links — even if a photo exists, there's still personalizing to do.
  const profileIsEmpty = previewBlank || !(
    profile.bio ||
    (profile.social_links && Object.values(profile.social_links).some(v => v))
  );
  let storedFirstName = '';
  try {
    storedFirstName = JSON.parse(localStorage.getItem('user') || '{}').first_name || '';
  } catch {}
  const displayTitle = getCollectionDisplayTitle(profile, storedFirstName, {
    forceGenerated: previewBlank,
  });

  // Soft placeholder avatar shown until the user uploads a photo, so the
  // profile area doesn't look oddly empty for brand-new accounts.
  const makeBlankAvatar = (sizeClass = 'w-20 h-20') => (
    <div
      className={`${sizeClass} rounded-full overflow-hidden flex-shrink-0`}
      data-testid="creator-photo-placeholder"
    >
      <img src="/avatar-smiley.svg" alt="" className="w-full h-full" draggable="false" />
    </div>
  );
  const blankAvatar = makeBlankAvatar();

  const openEditProfile = () => {
    if (subscriptionStatus === 'expired') {
      setShowUpgradeModal(true);
      return;
    }
    setProfileForm({
      ...profile,
      social_links: profile.social_links || {},
      social_display_names: profile.social_display_names || {}
    });
    setIsEditingProfile(true);
  };

  const emptyProfilePrompt = (
    <div
      className="inline-block rounded-lg px-4 py-3"
      style={{
        border: '1px dashed #C9BFA9',
        color: '#7B6159',
        fontFamily: "'Bricolage Grotesque', sans-serif",
        fontSize: '15px',
      }}
      data-testid="empty-profile-prompt"
    >
      Add a photo, short bio, and social links to personalize your recipe page.
    </div>
  );

  const editProfileLink = (
    <button
      onClick={openEditProfile}
      className="underline hover:opacity-70 mt-4 block"
      style={{
        fontSize: '16px', // match category filter links
        color: '#875C66',
        fontFamily: "'Bricolage Grotesque', sans-serif",
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer'
      }}
      data-testid="edit-profile-link"
    >
      Edit Profile
    </button>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#EEE7D8' }} data-testid="recipes-page">
      <PaymentConfirmationDialog
        state={paymentConfirmationState}
        onClose={closePaymentConfirmation}
        onRetry={() => setPaymentConfirmationAttempt((attempt) => attempt + 1)}
      />
      {subscriptionStatus === 'expired' && (
        <div
          className="px-4"
        >
          <div
            className="max-w-7xl mx-auto pl-4 pr-2 py-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
            style={{ backgroundColor: '#51120A' }}
          >
            <p className="text-sm" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#EEE7D8' }}>
              Your free trial has ended. Upgrade to keep creating and editing recipes.
            </p>
            <Button
              onClick={() => setShowUpgradeModal(true)}
              className="text-sm px-3 py-1.5 whitespace-nowrap"
              style={{
                backgroundColor: '#EEE7D8',
                color: '#51120A',
                fontFamily: "'Bricolage Grotesque', sans-serif",
              }}
            >
              Upgrade Now
            </Button>
          </div>
        </div>
      )}
      {subscriptionStatus === 'trialing' && trialDaysRemaining !== null && trialDaysRemaining <= 14 && (
        <div
          className="px-4"
        >
          <div
            className="max-w-7xl mx-auto pl-4 pr-2 py-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
            style={{ backgroundColor: '#51120A' }}
          >
            <p className="text-sm" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#EEE7D8' }}>
              {trialDaysRemaining === 0
                ? "Your free trial ends today!"
                : `Your free trial ends in ${trialDaysRemaining} day${trialDaysRemaining === 1 ? '' : 's'}.`}
            </p>
            <Button
              onClick={() => setShowUpgradeModal(true)}
              className="text-sm px-3 py-1.5 whitespace-nowrap"
              style={{
                backgroundColor: '#EEE7D8',
                color: '#51120A',
                fontFamily: "'Bricolage Grotesque', sans-serif",
              }}
            >
              Upgrade Now
            </Button>
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-4 py-16">
        {/* Header */}
        <div className="mb-8">
          {/* Mobile Layout */}
          {isMobile ? (
            <>
              {/* Owner Dashboard link (mobile) */}
              {adminToken && profile?.is_owner && !isEditingProfile && (
                <div className="mb-3">
                  <button
                    onClick={() => setShowOwnerDashboard(true)}
                    className="text-sm underline hover:opacity-70"
                    style={{
                      color: '#875C66',
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    Owner Dashboard
                  </button>
                </div>
              )}

              {/* CTAs at the top on mobile */}
              <div className="flex gap-3 mb-4 justify-end">
                {adminToken ? (
                  <>
                    {!isEditingProfile && (
                      <>
                        {/* Copy Link - Hollow Circle with Link Icon */}
                        <button
                          onClick={handleShareCollection}
                          data-testid="share-collection-btn"
                          className="flex items-center justify-center transition-colors group copy-link-circle"
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            border: '1px solid #51120A',
                            backgroundColor: 'transparent',
                            cursor: 'pointer'
                          }}
                          title="Copy Link"
                        >
                          <Link className="h-4 w-4 group-hover:text-[#51120A]" style={{ color: '#51120A' }} />
                        </button>

                        {subscriptionStatus === 'trialing' && (
                          <button
                            onClick={() => setShowUpgradeModal(true)}
                            className="px-4 py-2 text-sm font-medium rounded-lg transition-colors hover-blush"
                            style={{
                              backgroundColor: 'transparent',
                              border: '1px solid #51120A',
                              color: '#51120A',
                              fontFamily: "'Bricolage Grotesque', sans-serif",
                            }}
                          >
                            Upgrade
                          </button>
                        )}

                        {/* Profile Photo Circle with Dropdown */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              className="transition-opacity hover:opacity-80"
                              style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                overflow: 'hidden',
                                cursor: 'pointer',
                                border: 'none',
                                padding: 0,
                                backgroundColor: 'transparent'
                              }}
                              title="Account Menu"
                            >
                              {profile.creator_photo_url ? (
                                <img
                                  src={profile.creator_photo_url}
                                  alt={profile.creator_name || "Profile"}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                  }}
                                />
                              ) : (
                                <img
                                  src="/avatar-smiley.svg"
                                  alt=""
                                  style={{ width: '100%', height: '100%' }}
                                  draggable="false"
                                />
                              )}
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            style={{
                              backgroundColor: '#F3EEE4',
                              border: '1px solid #D6D0C2',
                              minWidth: '160px',
                              boxShadow: 'none'
                            }}
                          >
                            <DropdownMenuItem
                              onClick={() => navigate('/account')}
                              className="hover:bg-[#EEE7D8] cursor-pointer"
                              style={{
                                color: '#51120A',
                                fontFamily: "'Bricolage Grotesque', sans-serif",
                                padding: '8px 12px'
                              }}
                            >
                              Account
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                if (subscriptionStatus === 'expired') {
                                  setShowUpgradeModal(true);
                                  return;
                                }
                                setProfileForm({
                                  ...profile,
                                  social_links: profile.social_links || {},
                                  social_display_names: profile.social_display_names || {}
                                });
                                setIsEditingProfile(true);
                              }}
                              data-testid="edit-profile-btn"
                              className="hover:bg-[#EEE7D8] cursor-pointer"
                              style={{
                                color: '#51120A',
                                fontFamily: "'Bricolage Grotesque', sans-serif",
                                padding: '8px 12px'
                              }}
                            >
                              Edit Profile
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={handleLogout}
                              data-testid="admin-logout-btn"
                              className="hover:bg-[#EEE7D8] cursor-pointer"
                              style={{
                                color: '#51120A',
                                fontFamily: "'Bricolage Grotesque', sans-serif",
                                padding: '8px 12px'
                              }}
                            >
                              Logout
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </>
                    )}
                  </>
                ) : (
                  <>
                  {/* Public view - just show share button as circle */}
                  <button
                    onClick={handleShareCollection}
                    data-testid="share-collection-btn"
                    className="flex items-center justify-center transition-colors group copy-link-circle"
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      border: '1px solid #51120A',
                      backgroundColor: 'transparent',
                      cursor: 'pointer'
                    }}
                    title="Share"
                  >
                    <Share2 className="h-4 w-4 group-hover:text-[#51120A]" style={{ color: '#51120A' }} />
                  </button>
                  </>
                )}
              </div>

              {/* Collection Title and Profile */}
              {!isEditingProfile && (
                <>
                  <h1 className="text-4xl font-bold mb-6" style={{ fontFamily: "'Instrument Serif', serif", color: '#51120A' }} data-testid="page-title">
                    {displayTitle}
                  </h1>

                  {(
                    /* Mobile: photo stacked above the name, everything
                       left-aligned; photo 20% smaller (80px -> 64px).
                       Always rendered so the blank headshot persists. */
                    <div className="flex flex-col items-start gap-3 mb-6">
                      {!previewBlank && profile.creator_photo_url ? (
                        <img
                          src={profile.creator_photo_url}
                          alt={profile.creator_name || "Creator"}
                          className="w-16 h-16 rounded-full object-cover"
                          data-testid="creator-photo"
                        />
                      ) : makeBlankAvatar('w-16 h-16')}
                      <div className="w-full">
                        {profile.creator_name && (
                          <h3 className="text-xl font-bold mb-2" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }} data-testid="creator-name">
                            {profile.creator_name}
                          </h3>
                        )}
                        {profileIsEmpty && emptyProfilePrompt}
                        {!profileIsEmpty && profile.bio && (
                          <p style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }} data-testid="profile-bio">
                            {profile.bio}
                          </p>
                        )}
                        {/* Social Links Tags */}
                        {!profileIsEmpty && profile.social_links && Object.keys(profile.social_links).filter(key => profile.social_links[key]).length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {Object.entries(profile.social_links).filter(([_, url]) => url).map(([platform, url]) => {
                              // Extract handle or title from URL, or use custom display name
                              const getDisplayText = (platform, url) => {
                                // Check if there's a custom display name
                                if (profile.social_display_names && profile.social_display_names[platform]) {
                                  return profile.social_display_names[platform];
                                }

                                try {
                                  const urlObj = new URL(url);
                                  const pathname = urlObj.pathname;

                                  if (platform === 'instagram' || platform === 'tiktok' || platform === 'x' || platform === 'threads') {
                                    // Extract @handle
                                    const handle = pathname.split('/').filter(p => p)[0];
                                    return handle ? `@${handle}` : platform;
                                  } else if (platform === 'substack') {
                                    // Extract newsletter name from subdomain
                                    const subdomain = urlObj.hostname.split('.')[0];
                                    return subdomain !== 'www' ? subdomain : 'Newsletter';
                                  } else if (platform === 'youtube') {
                                    // Extract channel name
                                    const parts = pathname.split('/').filter(p => p);
                                    return parts.length > 0 ? parts[parts.length - 1] : 'YouTube';
                                  } else if (platform === 'website') {
                                    return urlObj.hostname.replace('www.', '');
                                  } else {
                                    return platform.charAt(0).toUpperCase() + platform.slice(1);
                                  }
                                } catch {
                                  return platform.charAt(0).toUpperCase() + platform.slice(1);
                                }
                              };

                              const getIcon = (platform) => {
                                const iconSize = 14;
                                const iconColor = '#51120A';
                                switch(platform) {
                                  case 'instagram': return <Instagram size={iconSize} color={iconColor} />;
                                  case 'tiktok': return <TikTokIcon size={iconSize} color={iconColor} />;
                                  case 'youtube': return <Youtube size={iconSize} color={iconColor} />;
                                  case 'facebook': return <Facebook size={iconSize} color={iconColor} />;
                                  case 'x': return <XIcon size={iconSize} color={iconColor} />;
                                  case 'threads': return <ThreadsIcon size={iconSize} color={iconColor} />;
                                  case 'substack': return <SubstackIcon size={iconSize} color={iconColor} />;
                                  case 'website': return <Globe size={iconSize} color={iconColor} />;
                                  default: return null;
                                }
                              };

                              return (
                                <a
                                  key={platform}
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="px-3 py-1 rounded text-sm transition-colors hover:opacity-80 flex items-center gap-1.5"
                                  style={{ backgroundColor: '#F3EEE4', color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", textDecoration: 'none' }}
                                >
                                  {getIcon(platform)}
                                  {getDisplayText(platform, url)}
                                </a>
                              );
                            })}
                          </div>
                        )}
                        {editProfileLink}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            /* Desktop/Tablet Layout */
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1">
                {!isEditingProfile && (
                  <>
                    {/* Collection Title - Top */}
                    <h1 className="text-5xl font-bold mb-8" style={{ fontFamily: "'Instrument Serif', serif", color: '#51120A' }} data-testid="page-title">
                      {displayTitle}
                    </h1>

                    {/* Profile Section - Headshot with Name and Bio.
                        Always rendered so the blank headshot persists. */}
                    {(
                      <div className="flex items-start gap-4">
                        {!previewBlank && profile.creator_photo_url ? (
                          <img
                            src={profile.creator_photo_url}
                            alt={profile.creator_name || "Creator"}
                            className="w-20 h-20 rounded-full object-cover"
                            data-testid="creator-photo"
                          />
                        ) : blankAvatar}
                        <div className="flex-1">
                          {profile.creator_name && (
                            <h3 className="text-xl font-bold mb-2" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }} data-testid="creator-name">
                              {profile.creator_name}
                            </h3>
                          )}
                          {profileIsEmpty && emptyProfilePrompt}
                          {!profileIsEmpty && profile.bio && (
                            <p className="md:max-w-md" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }} data-testid="profile-bio">
                              {profile.bio}
                            </p>
                          )}
                          {/* Social Links Tags */}
                          {!profileIsEmpty && profile.social_links && Object.keys(profile.social_links).filter(key => profile.social_links[key]).length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3">
                              {Object.entries(profile.social_links).filter(([_, url]) => url).map(([platform, url]) => {
                                // Extract handle or title from URL, or use custom display name
                                const getDisplayText = (platform, url) => {
                                  // Check if there's a custom display name
                                  if (profile.social_display_names && profile.social_display_names[platform]) {
                                    return profile.social_display_names[platform];
                                  }

                                  try {
                                    const urlObj = new URL(url);
                                    const pathname = urlObj.pathname;

                                    if (platform === 'instagram' || platform === 'tiktok' || platform === 'x' || platform === 'threads') {
                                      // Extract @handle
                                      const handle = pathname.split('/').filter(p => p)[0];
                                      return handle ? `@${handle}` : platform;
                                    } else if (platform === 'substack') {
                                      // Extract newsletter name from subdomain
                                      const subdomain = urlObj.hostname.split('.')[0];
                                      return subdomain !== 'www' ? subdomain : 'Newsletter';
                                    } else if (platform === 'youtube') {
                                      // Extract channel name
                                      const parts = pathname.split('/').filter(p => p);
                                      return parts.length > 0 ? parts[parts.length - 1] : 'YouTube';
                                    } else if (platform === 'website') {
                                      return urlObj.hostname.replace('www.', '');
                                    } else {
                                      return platform.charAt(0).toUpperCase() + platform.slice(1);
                                    }
                                  } catch {
                                    return platform.charAt(0).toUpperCase() + platform.slice(1);
                                  }
                                };

                                const getIcon = (platform) => {
                                  const iconSize = 14;
                                  const iconColor = '#51120A';
                                  switch(platform) {
                                    case 'instagram': return <Instagram size={iconSize} color={iconColor} />;
                                    case 'tiktok': return <TikTokIcon size={iconSize} color={iconColor} />;
                                    case 'youtube': return <Youtube size={iconSize} color={iconColor} />;
                                    case 'facebook': return <Facebook size={iconSize} color={iconColor} />;
                                    case 'x': return <XIcon size={iconSize} color={iconColor} />;
                                  case 'threads': return <ThreadsIcon size={iconSize} color={iconColor} />;
                                    case 'substack': return <SubstackIcon size={iconSize} color={iconColor} />;
                                    case 'website': return <Globe size={iconSize} color={iconColor} />;
                                    default: return null;
                                  }
                                };

                                return (
                                  <a
                                    key={platform}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-3 py-1 rounded text-sm transition-colors hover:opacity-80 flex items-center gap-1.5"
                                    style={{ backgroundColor: '#F3EEE4', color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", textDecoration: 'none' }}
                                  >
                                    {getIcon(platform)}
                                    {getDisplayText(platform, url)}
                                  </a>
                                );
                              })}
                            </div>
                          )}
                          {editProfileLink}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="flex items-center gap-6">
                {/* Owner Dashboard link (desktop) */}
                {adminToken && profile?.is_owner && !isEditingProfile && (
                  <button
                    onClick={() => setShowOwnerDashboard(true)}
                    className="text-sm underline hover:opacity-70"
                    style={{
                      color: '#875C66',
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    Owner Dashboard
                  </button>
                )}

                <div className="flex gap-3 items-center">
                {adminToken ? (
                  <>
                    {!isEditingProfile && (
                      <>
                        {/* Copy Link - Hollow Circle with Link Icon */}
                        <button
                          onClick={handleShareCollection}
                          data-testid="share-collection-btn"
                          className="flex items-center justify-center transition-colors group copy-link-circle"
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            border: '1px solid #51120A',
                            backgroundColor: 'transparent',
                            cursor: 'pointer'
                          }}
                          title="Copy Link"
                        >
                          <Link className="h-4 w-4 group-hover:text-[#51120A]" style={{ color: '#51120A' }} />
                        </button>

                        {subscriptionStatus === 'trialing' && (
                          <button
                            onClick={() => setShowUpgradeModal(true)}
                            className="px-4 py-2 text-sm font-medium rounded-lg transition-colors hover-blush"
                            style={{
                              backgroundColor: 'transparent',
                              border: '1px solid #51120A',
                              color: '#51120A',
                              fontFamily: "'Bricolage Grotesque', sans-serif",
                            }}
                          >
                            Upgrade
                          </button>
                        )}

                        {/* Profile Photo Circle with Dropdown */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              className="transition-opacity hover:opacity-80"
                              style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                overflow: 'hidden',
                                cursor: 'pointer',
                                border: 'none',
                                padding: 0,
                                backgroundColor: 'transparent'
                              }}
                              title="Account Menu"
                            >
                              {profile.creator_photo_url ? (
                                <img
                                  src={profile.creator_photo_url}
                                  alt={profile.creator_name || "Profile"}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                  }}
                                />
                              ) : (
                                <img
                                  src="/avatar-smiley.svg"
                                  alt=""
                                  style={{ width: '100%', height: '100%' }}
                                  draggable="false"
                                />
                              )}
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            style={{
                              backgroundColor: '#F3EEE4',
                              border: '1px solid #D6D0C2',
                              minWidth: '160px',
                              boxShadow: 'none'
                            }}
                          >
                            <DropdownMenuItem
                              onClick={() => navigate('/account')}
                              className="hover:bg-[#EEE7D8] cursor-pointer"
                              style={{
                                color: '#51120A',
                                fontFamily: "'Bricolage Grotesque', sans-serif",
                                padding: '8px 12px'
                              }}
                            >
                              Account
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                if (subscriptionStatus === 'expired') {
                                  setShowUpgradeModal(true);
                                  return;
                                }
                                setProfileForm({
                                  ...profile,
                                  social_links: profile.social_links || {},
                                  social_display_names: profile.social_display_names || {}
                                });
                                setIsEditingProfile(true);
                              }}
                              data-testid="edit-profile-btn"
                              className="hover:bg-[#EEE7D8] cursor-pointer"
                              style={{
                                color: '#51120A',
                                fontFamily: "'Bricolage Grotesque', sans-serif",
                                padding: '8px 12px'
                              }}
                            >
                              Edit Profile
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={handleLogout}
                              data-testid="admin-logout-btn"
                              className="hover:bg-[#EEE7D8] cursor-pointer"
                              style={{
                                color: '#51120A',
                                fontFamily: "'Bricolage Grotesque', sans-serif",
                                padding: '8px 12px'
                              }}
                            >
                              Logout
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                    </>
                  )}
                </>
              ) : (
                <>
                  {/* Public view - just show share button as circle */}
                  <button
                    onClick={handleShareCollection}
                    data-testid="share-collection-btn"
                    className="flex items-center justify-center transition-colors group copy-link-circle"
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      border: '1px solid #51120A',
                      backgroundColor: 'transparent',
                      cursor: 'pointer'
                    }}
                    title="Share"
                  >
                    <Share2 className="h-4 w-4 group-hover:text-[#51120A]" style={{ color: '#51120A' }} />
                  </button>
                </>
              )}
              </div>
            </div>
          </div>
          )}

          {/* Pinned Recipes Section - Above Filters */}
          {adminToken && recipes.filter(r => r.pinned).length > 0 && (
            <div className="mb-8 mt-5">
              <h2 className="text-xl font-bold mb-6" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                Pinned Recipes
              </h2>
              <RecipeCollectionGrid className="mb-8">
                {recipes.filter(r => r.pinned).map(recipe => (
                  <div key={recipe.id}>
                    <RecipeCard
                      recipe={recipe}
                      isAdmin={!!adminToken}
                      onEdit={handleEditRecipe}
                      onDelete={handleDeleteRecipe}
                      onPin={handleTogglePin}
                      showPinnedShortcut
                      username={profile?.username}
                      onClickInterceptor={requireActiveSubscription}
                    onBeforeNavigate={saveReturnState}
                    />
                  </div>
                ))}
              </RecipeCollectionGrid>

              {/* Mobile pinned */}
              <div className="md:hidden block space-y-6 mb-8">
                {recipes.filter(r => r.pinned).map(recipe => (
                  <RecipeCard
                    key={recipe.id}
                    recipe={recipe}
                    isAdmin={!!adminToken}
                    onEdit={handleEditRecipe}
                    onDelete={handleDeleteRecipe}
                    onPin={handleTogglePin}
                    showPinnedShortcut
                    username={profile?.username}
                    onClickInterceptor={requireActiveSubscription}
                    onBeforeNavigate={saveReturnState}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Filter Bar with Add Recipe Button and Search — hidden while the
              collection is empty (first-time login / back to 0 recipes): the
              only action on screen should be the "Save your first recipe" CTA. */}
          {recipes.length > 0 && (isMobile ? (
            /* Mobile Layout */
            <div className="mb-8">
              {/* Category filters - left-aligned, wrapping on two lines */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-4" data-testid="category-filter">
                <span className="inline-flex items-center gap-3">
                <button
                  onClick={() => setSelectedCategory("all")}
                  className={`transition-colors ${selectedCategory === "all" ? 'underline' : 'hover:text-[#875C66]'}`}
                  style={{
                    color: selectedCategory === "all" ? '#875C66' : '#51120A',
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    textDecorationColor: selectedCategory === "all" ? '#875C66' : 'transparent',
                    textUnderlineOffset: '4px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '16px',
                    textAlign: 'left'
                  }}
                  data-testid="filter-btn-all"
                >
                  All Recipes
                </button>
                <span style={{ color: '#D6D0C2', fontSize: '20px' }}>|</span>
                </span>
                {categories.map((cat, index) => (
                  <span key={cat.id} className="inline-flex items-center gap-3">
                    <button
                      onClick={() => setSelectedCategory(cat.name.toLowerCase())}
                      className={`transition-colors ${selectedCategory === cat.name.toLowerCase() ? 'underline' : 'hover:text-[#875C66]'}`}
                      style={{
                        color: selectedCategory === cat.name.toLowerCase() ? '#875C66' : '#51120A',
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                        textDecorationColor: selectedCategory === cat.name.toLowerCase() ? '#875C66' : 'transparent',
                        textUnderlineOffset: '4px',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '16px',
                        textAlign: 'left'
                      }}
                      data-testid={`filter-btn-${cat.id}`}
                    >
                      {cat.name}
                    </button>
                    {index < categories.length - 1 && (
                      <span style={{ color: '#D6D0C2', fontSize: '20px' }}>|</span>
                    )}
                  </span>
                ))}
              </div>

              {/* Add Recipe Button - full width */}
              {adminToken && (
                <Button
                  onClick={() => {
                    if (!requireActiveSubscription()) return;
                    setEditingRecipe(null);
                    setShowRecipeForm(true);
                  }}
                  data-testid="add-recipe-btn-filter-line"
                  className="focus:outline-none focus:ring-0 w-full mb-4"
                  style={{ backgroundColor: '#51120A', borderColor: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
                >
                  Add Recipe
                </Button>
              )}

              {/* Search Bar - full width */}
              <Input
                type="text"
                placeholder="Search Recipes"
                value={searchInput}
                onChange={handleSearchChange}
                className="w-full border-0 border-b rounded-none focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                style={{
                  borderColor: '#51120A',
                  borderBottomWidth: '1px',
                  paddingLeft: '0',
                  backgroundColor: 'transparent',
                  color: '#51120A',
                  fontSize: '16px',
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  boxShadow: 'none'
                }}
                data-testid="search-input"
              />
            </div>
          ) : (
            /* Desktop/Tablet Layout */
            <div className="flex justify-between items-end mb-8 gap-10">
              {/* Filter Bar - Text Style with Dividers */}
              <div className="flex items-center gap-4 flex-wrap" data-testid="category-filter">
                <span className="inline-flex items-center gap-4">
                <button
                  onClick={() => setSelectedCategory("all")}
                  className={`transition-colors ${selectedCategory === "all" ? 'underline' : 'hover:text-[#875C66]'}`}
                  style={{
                    color: selectedCategory === "all" ? '#875C66' : '#51120A',
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    textDecorationColor: selectedCategory === "all" ? '#875C66' : 'transparent',
                    textUnderlineOffset: '4px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '16px'
                  }}
                  data-testid="filter-btn-all"
                >
                  All Recipes
                </button>
                <span style={{ color: '#D6D0C2', fontSize: '20px' }}>|</span>
                </span>
                {categories.map((cat, index) => (
                  <span key={cat.id} className="inline-flex items-center gap-4">
                    <button
                      onClick={() => setSelectedCategory(cat.name.toLowerCase())}
                      className={`transition-colors ${selectedCategory === cat.name.toLowerCase() ? 'underline' : 'hover:text-[#875C66]'}`}
                      style={{
                        color: selectedCategory === cat.name.toLowerCase() ? '#875C66' : '#51120A',
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                        textDecorationColor: selectedCategory === cat.name.toLowerCase() ? '#875C66' : 'transparent',
                        textUnderlineOffset: '4px',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '16px'
                      }}
                      data-testid={`filter-btn-${cat.id}`}
                    >
                      {cat.name}
                    </button>
                    {index < categories.length - 1 && (
                      <span style={{ color: '#D6D0C2', fontSize: '20px' }}>|</span>
                    )}
                  </span>
                ))}
              </div>

              {/* Right side: Add Recipe Button and Search Bar */}
              <div className="flex flex-col items-end gap-2">
                {/* Add Recipe Button */}
                {adminToken && (
                  <Button
                    onClick={() => {
                      if (!requireActiveSubscription()) return;
                      setEditingRecipe(null);
                      setShowRecipeForm(true);
                    }}
                    data-testid="add-recipe-btn-filter-line"
                    className="focus:outline-none focus:ring-0"
                    style={{ backgroundColor: '#51120A', borderColor: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
                  >
                    Add Recipe
                  </Button>
                )}

                {/* Search Bar */}
                <Input
                  type="text"
                  placeholder="Search Recipes"
                  value={searchInput}
                  onChange={handleSearchChange}
                  className="w-64 border-0 border-b rounded-none focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                  style={{
                    borderColor: '#51120A',
                    borderBottomWidth: '1px',
                    paddingLeft: '0',
                    backgroundColor: 'transparent',
                    color: '#51120A',
                    fontSize: '16px',
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    boxShadow: 'none'
                  }}
                  data-testid="search-input"
                />
              </div>
            </div>
          ))}
        </div>

        {/* Recipe Form Dialog - Only for Create New */}
        {showRecipeForm && !editingRecipe && (
          <Dialog open={showRecipeForm} onOpenChange={(open) => { if (!open) { setShowRecipeForm(false); return; } setShowRecipeForm(open); }}>
            <DialogContent className="max-w-2xl" style={{ backgroundColor: '#F3EEE4', border: 'none' }} onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()}>
              <RecipeForm
                recipe={null}
                onSave={handleSaveRecipe}
                onCancel={() => {
                  setShowRecipeForm(false);
                }}
                adminToken={adminToken}
                isEditing={false}
                categories={categories}
                onManageCategories={() => setShowManageCategories(true)}
              />
            </DialogContent>
          </Dialog>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent
            data-testid="delete-confirmation-dialog"
            overlayClassName="bg-[#EEE7D8]/60 backdrop-blur-sm"
            style={{ backgroundColor: '#F3EEE4', border: '1px solid #D6D0C2', boxShadow: 'none' }}
          >
            <DialogHeader>
              <DialogTitle style={{ color: '#51120A', fontFamily: "'Instrument Serif', serif", fontSize: '24px' }}>
                Delete Recipe
              </DialogTitle>
              <DialogDescription style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                Are you sure you'd like to delete this?
              </DialogDescription>
            </DialogHeader>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteDialog(false);
                  setRecipeToDelete(null);
                }}
                data-testid="delete-cancel-btn"
                style={{
                  borderWidth: '1px',
                  borderColor: '#51120A',
                  color: '#51120A',
                  backgroundColor: 'transparent',
                  fontFamily: "'Bricolage Grotesque', sans-serif"
                }}
                className="hover:bg-[#EEE7D8] transition-colors"
              >
                No
              </Button>
              <Button
                onClick={confirmDelete}
                data-testid="delete-confirm-btn"
                style={{
                  backgroundColor: '#51120A',
                  color: 'white',
                  fontFamily: "'Bricolage Grotesque', sans-serif"
                }}
                className="hover:bg-[#3d0d07] transition-colors"
              >
                Yes
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* First-recipe empty state: sketched placeholder collage + a live
            "Save your first recipe" CTA. Shows whenever the collection has 0
            recipes (first-time login, or after deleting back down to zero)
            and vanishes the moment the first real recipe exists. The CTA is
            positioned with percentages inside the same aspect-ratio box as
            the artwork, so it scales and moves WITH the collage on resize —
            never floating off as a separate thing. */}
        {recipes.length === 0 && (
          <EmptyCollectionState
            heading="Save your first recipe"
            onAddRecipe={() => {
              if (!requireActiveSubscription()) return;
              setEditingRecipe(null);
              setShowRecipeForm(true);
            }}
          />
        )}

        {/* Filtered Recipes Grid */}
        <RecipeCollectionGrid
          style={{
            minHeight: '200px',
          }}
          data-testid="recipes-grid"
        >
          {filteredRecipes.map(recipe => (
            <div key={recipe.id}>
              <RecipeCard
                recipe={recipe}
                isAdmin={!!adminToken}
                showPinnedShortcut
                onEdit={handleEditRecipe}
                onDelete={handleDeleteRecipe}
                onPin={handleTogglePin}
                username={profile?.username}
                onClickInterceptor={requireActiveSubscription}
                    onBeforeNavigate={saveReturnState}
              />
            </div>
          ))}
        </RecipeCollectionGrid>

        {/* Mobile: Single column */}
        <div className="md:hidden block space-y-6" data-testid="recipes-grid-mobile">
          {filteredRecipes.map(recipe => (
            <RecipeCard
              key={recipe.id}
              recipe={recipe}
              isAdmin={!!adminToken}
              showPinnedShortcut
              onEdit={handleEditRecipe}
              onDelete={handleDeleteRecipe}
              onPin={handleTogglePin}
              username={profile?.username}
              onClickInterceptor={requireActiveSubscription}
                    onBeforeNavigate={saveReturnState}
            />
          ))}
        </div>

        {recipes.length > 0 && filteredRecipes.length === 0 && (
          <div className="text-center py-12" data-testid="no-recipes-message">
            <p className="style={{ color: '#51120A' }} text-lg">No recipes found in this category</p>
          </div>
        )}

        {/* Manage Categories Dialog */}
        <Dialog open={showManageCategories} onOpenChange={(open) => {
          setShowManageCategories(open);
          if (!open) {
            // Refresh categories when dialog closes
            fetchCategories();
          }
        }}>
          <DialogContent className="sm:max-w-[500px]" style={{ backgroundColor: '#F3EEE4', border: '1px solid #D6D0C2', boxShadow: 'none' }}>
            <DialogHeader>
              <DialogTitle style={{ color: '#51120A', fontFamily: "'Instrument Serif', serif", fontSize: '32px', lineHeight: 1.1 }}>
                Manage Categories
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Add New Category Input */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                  Add New Category
                </label>
                <div className="flex gap-2">
                  <Input
                    value={newCategoryInput}
                    onChange={(e) => setNewCategoryInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleCreateCategory();
                      }
                    }}
                    placeholder="Type and press Enter"
                    className="flex-1"
                    style={{
                      backgroundColor: '#EEE7D8',
                      borderColor: '#D6D0C2',
                      color: '#51120A',
                      fontFamily: "'Bricolage Grotesque', sans-serif"
                    }}
                  />
                </div>
                <p className="text-sm mt-1" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", opacity: 0.7 }}>
                  Press Enter to create a new category
                </p>
              </div>

              {/* Categories List */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                  Your Categories
                </label>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={categories.map(cat => cat.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {categories.map((category) => (
                        <SortableCategoryItem
                          key={category.id}
                          category={category}
                          editingCategoryId={editingCategoryId}
                          editingCategoryName={editingCategoryName}
                          setEditingCategoryId={setEditingCategoryId}
                          setEditingCategoryName={setEditingCategoryName}
                          handleUpdateCategory={handleUpdateCategory}
                          handleDeleteCategory={handleDeleteCategory}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                onClick={() => setShowManageCategories(false)}
                style={{ backgroundColor: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
              >
                Done
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Category Confirmation Dialog */}
        <Dialog open={deleteCategoryDialog.open} onOpenChange={(open) => !open && setDeleteCategoryDialog({ open: false, category: null, recipeCount: 0 })}>
          <DialogContent className="sm:max-w-[425px]" overlayClassName="bg-[#EEE7D8]/60 backdrop-blur-sm" style={{ backgroundColor: '#F3EEE4', border: '1px solid #D6D0C2', boxShadow: 'none' }}>
            <DialogHeader>
              <DialogTitle style={{ color: '#51120A', fontFamily: "'Instrument Serif', serif", fontSize: '24px' }}>
                Delete Category
              </DialogTitle>
            </DialogHeader>

            <div className="py-4">
              {deleteCategoryDialog.recipeCount > 0 ? (
                <p style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", lineHeight: '1.6' }}>
                  Are you sure you want to delete this tag? This tag is present on{' '}
                  <strong>{deleteCategoryDialog.recipeCount}</strong> recipe{deleteCategoryDialog.recipeCount > 1 ? 's' : ''}.
                  <br /><br />
                  Recipes will be moved to "uncategorized".
                </p>
              ) : (
                <p style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", lineHeight: '1.6' }}>
                  Are you sure you want to delete this category?
                </p>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setDeleteCategoryDialog({ open: false, category: null, recipeCount: 0 })}
                style={{
                  borderWidth: '1px',
                  borderColor: '#51120A',
                  color: '#51120A',
                  fontFamily: "'Bricolage Grotesque', sans-serif"
                }}
                className="hover-blush transition-colors"
              >
                No
              </Button>
              <Button
                onClick={confirmDeleteCategory}
                style={{
                  backgroundColor: '#51120A',
                  fontFamily: "'Bricolage Grotesque', sans-serif"
                }}
              >
                Yes, Delete
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Profile Dialog */}
        {isEditingProfile && (
          <Dialog open={isEditingProfile} onOpenChange={setIsEditingProfile}>
            <DialogContent className="max-w-2xl" style={{ backgroundColor: '#F3EEE4', border: 'none' }}>
              <div className="space-y-4">
                <h2 style={{ fontSize: '36px', fontFamily: "'Instrument Serif', serif", color: '#51120A', margin: 0, marginBottom: '24px', lineHeight: 1.1 }}>
                  Edit Profile
                </h2>

                {/* Profile Photo */}
                <div className="flex items-center gap-4">
                  <div className="relative group cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                    <div
                      className="w-20 h-20 rounded-full overflow-hidden flex items-center justify-center"
                      style={{ backgroundColor: '#D6D0C2' }}
                    >
                      {profileForm.creator_photo_url ? (
                        <img
                          src={profileForm.creator_photo_url}
                          alt={profileForm.creator_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <img src="/avatar-smiley.svg" alt="" className="w-full h-full" draggable="false" />
                      )}
                    </div>
                    <div
                      className="absolute bottom-0 right-0 w-7 h-7 rounded-full flex items-center justify-center transition-colors"
                      style={{ backgroundColor: '#51120A' }}
                    >
                      <Camera className="h-4 w-4" style={{ color: '#EEE7D8' }} />
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                      data-testid="profile-photo-input"
                    />
                  </div>
                  <Label style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>Profile Photo</Label>
                </div>

                {/* Collection Title */}
                <div>
                  <Label style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>Collection Title</Label>
                  <Input
                    value={profileForm.collection_title}
                    onChange={(e) => setProfileForm({ ...profileForm, collection_title: e.target.value })}
                    style={{ borderColor: '#D6D0C2', boxShadow: 'none', backgroundColor: 'transparent' }}
                    className="focus:ring-0 focus:border-[#D6D0C2]"
                    data-testid="profile-title-input"
                  />
                </div>

                {/* Your Name */}
                <div>
                  <Label style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>Your Name</Label>
                  <Input
                    value={profileForm.creator_name || ''}
                    onChange={(e) => setProfileForm({ ...profileForm, creator_name: e.target.value })}
                    placeholder="Your name"
                    style={{ borderColor: '#D6D0C2', boxShadow: 'none', backgroundColor: 'transparent' }}
                    className="focus:ring-0 focus:border-[#D6D0C2]"
                    data-testid="creator-name-input"
                  />
                </div>

                {/* Bio */}
                <div>
                  <Label style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>Bio (max 120 characters)</Label>
                  <Textarea
                    value={profileForm.bio || ''}
                    onChange={(e) => setProfileForm({ ...profileForm, bio: e.target.value })}
                    placeholder="Add a bio..."
                    maxLength={120}
                    rows={2}
                    style={{ borderColor: '#D6D0C2', boxShadow: 'none', backgroundColor: 'transparent' }}
                    className="focus:ring-0 focus:border-[#D6D0C2]"
                    data-testid="profile-bio-input"
                  />
                </div>

                {/* Social Links */}
                <div>
                  <Label style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", marginBottom: '8px', display: 'block' }}>Social Links (optional)</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { key: 'instagram', label: 'Instagram', icon: <Instagram size={16} /> },
                      { key: 'tiktok', label: 'TikTok', icon: <TikTokIcon size={16} /> },
                      { key: 'youtube', label: 'YouTube', icon: <Youtube size={16} /> },
                      { key: 'facebook', label: 'Facebook', icon: <Facebook size={16} /> },
                      { key: 'x', label: 'X', icon: <XIcon size={16} /> },
                      { key: 'threads', label: 'Threads', icon: <ThreadsIcon size={16} /> },
                      { key: 'substack', label: 'Substack', icon: <SubstackIcon size={16} /> },
                      { key: 'website', label: 'Website', icon: <Globe size={16} /> }
                    ].map(({ key, label, icon }) => (
                      <div key={key}>
                        <Label className="flex items-center gap-1.5 mb-1" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '14px' }}>
                          {icon}
                          {label}
                        </Label>
                        <Input
                          value={profileForm.social_links?.[key] || ''}
                          onChange={(e) => {
                            setProfileForm({
                              ...profileForm,
                              social_links: { ...(profileForm.social_links || {}), [key]: e.target.value }
                            });
                            if (linkErrors[key]) {
                              setLinkErrors({ ...linkErrors, [key]: false });
                            }
                          }}
                          placeholder={PLATFORM_PLACEHOLDERS[key] || 'URL'}
                          style={{ borderColor: linkErrors[key] ? '#B3261E' : '#D6D0C2', boxShadow: 'none', backgroundColor: 'transparent' }}
                          className={linkErrors[key] ? "focus:ring-0 focus:border-[#B3261E]" : "focus:ring-0 focus:border-[#D6D0C2]"}
                          data-testid={`social-link-input-${key}`}
                        />
                        {linkErrors[key] && (
                          <p className="mt-1" style={{ color: '#B3261E', fontSize: '12px', fontFamily: "'Bricolage Grotesque', sans-serif" }} data-testid={`social-link-error-${key}`}>
                            Invalid link
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex gap-2 justify-end pt-4">
                  <Button
                    variant="outline"
                    onClick={handleCancelEdit}
                    data-testid="cancel-profile-btn"
                    className="hover-blush transition-colors focus:outline-none focus:ring-0"
                    style={{ borderWidth: '1px', borderColor: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveProfile}
                    data-testid="save-profile-btn"
                    className="focus:outline-none focus:ring-0"
                    style={{ backgroundColor: '#51120A', borderColor: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
                  >
                    Save
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Owner Dashboard */}
        <OwnerDashboard
          open={showOwnerDashboard}
          onOpenChange={setShowOwnerDashboard}
        />

        {/* Upgrade Modal */}
        <UpgradeModal
          open={showUpgradeModal}
          onOpenChange={setShowUpgradeModal}
          subscriptionStatus={subscriptionStatus}
        />
      </div>
    </div>
  );
};

export default RecipeCollection;
